import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, UserPlus, MapPin, GraduationCap, Detective, Plus, MagnifyingGlassPlus, ArrowRight,
  ChatCircle, Clock, HandWaving, Camera, PaperPlaneRight, FunnelSimple, X, Bell, BellSlash,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const FIELDS = [
  ["q", "Name / nickname"], ["institution", "Institution"], ["course", "Course"],
  ["class_name", "Class"], ["grad_year", "Graduation year"], ["batch", "Batch"],
  ["location", "Location"], ["profession", "Profession"],
];

function SearchTab() {
  const [f, setF] = useState({});
  const [data, setData] = useState({ total: 0, results: [] });
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setSearched(true);
    try {
      const { data } = await api.get("/alumni", { params: f });
      setData(data);
    } finally { setLoading(false); }
  };

  const connect = async (id) => {
    try { await api.post("/connections", { addressee_id: id }); toast.success("Connection request sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Already connected"); }
  };

  return (
    <div>
      <Card className="border-border/60 p-6 shadow-sm">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && run()} data-testid={`find-${k}`} />
            </div>
          ))}
        </div>
        <Button className="mt-5 rounded-full gap-2" onClick={run} data-testid="find-search-btn"><MagnifyingGlass size={16} /> Search colleagues</Button>
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {loading && <p className="text-muted-foreground">Searching…</p>}
        {searched && !loading && data.results.length === 0 && (
          <p className="text-muted-foreground">No matches. Try fewer filters — or post a "Help me find someone" request in the next tab.</p>
        )}
        {data.results.map((p, i) => {
          const initials = (p.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
          const inst = p.education?.[0]?.institution;
          return (
            <Card key={p.user_id} className="border-border/60 p-6 shadow-sm animate-fade-up" style={{ animationDelay: `${i * 0.03}s` }} data-testid="find-result-card">
              <div className="flex items-start gap-4">
                <Avatar className="h-14 w-14"><AvatarImage src={fileUrl(p.avatar_file_id)} /><AvatarFallback className="bg-accent">{initials}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <Link to={`/u/${p.public_slug || p.user_id}`} className="block truncate font-heading text-lg font-medium hover:underline">{p.name}</Link>
                  <p className="truncate text-sm text-muted-foreground">{p.headline || "Alumnus"}</p>
                  {p.location && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={13} /> {p.location}</p>}
                </div>
              </div>
              {inst && <Badge variant="secondary" className="mt-4 gap-1 rounded-full font-normal"><GraduationCap size={13} /> {inst}</Badge>}
              <div className="mt-5 flex gap-2">
                <Link to={`/u/${p.public_slug || p.user_id}`} className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-full">View</Button></Link>
                <Button size="sm" className="flex-1 rounded-full gap-1.5" onClick={() => connect(p.user_id)}><UserPlus size={15} /> Connect</Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

const REQ_FIELDS = [
  ["subject_name", "Full name *"], ["nickname", "Nickname"], ["former_name", "Former / maiden name"],
  ["institution", "Institution"], ["course", "Course"], ["class_name", "Class"],
  ["grad_year", "Graduation year"], ["batch", "Batch"], ["last_known_location", "Last known location"],
  ["city", "City"], ["country", "Country"], ["profession", "Profession"], ["employer", "Employer"],
  ["known_friends", "Known friends"], ["known_relatives", "Known relatives"],
];

const REACTIONS = ["❤️", "🙏", "👍", "😢", "🎉"];

function CommentBubble({ c, reactComment, onReply }) {
  const ci = (c.user_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const rc = c.reaction_counts || {};
  const total = Object.values(rc).reduce((a, b) => a + b, 0);
  return (
    <div className="flex items-start gap-2.5">
      <Avatar className="h-8 w-8"><AvatarImage src={fileUrl(c.avatar_file_id)} /><AvatarFallback className="bg-accent text-[10px]">{ci}</AvatarFallback></Avatar>
      <div className="min-w-0 flex-1">
        <div className="inline-block rounded-2xl border border-border/60 bg-card px-3 py-2 text-sm">
          <span className="font-semibold">{c.user_name}</span> <span className="text-foreground/90">{c.text}</span>
        </div>
        <div className="mt-1 flex items-center gap-3 pl-1 text-xs text-muted-foreground">
          <div className="group relative">
            <button className={c.my_reaction ? "text-secondary" : "hover:text-foreground"} data-testid="comment-react-toggle">{c.my_reaction || "React"}</button>
            <div className="absolute bottom-full left-0 z-10 mb-1 hidden gap-1 rounded-full border border-border/60 bg-card p-1 shadow-lg group-hover:flex">
              {REACTIONS.map((e) => <button key={e} onClick={() => reactComment(c.id, e)} className="rounded-full px-1 text-base transition-transform hover:scale-125" data-testid={`comment-react-${e}`}>{e}</button>)}
            </div>
          </div>
          {onReply && <button className="hover:text-foreground" onClick={onReply} data-testid="comment-reply-btn">Reply</button>}
          {total > 0 && <span>{Object.entries(rc).map(([e, n]) => `${e}${n}`).join(" ")}</span>}
        </div>
      </div>
    </div>
  );
}

function CommentRow({ c, comments, reactComment, replyTo, setReplyTo, replyText, setReplyText, postComment }) {
  const replies = (comments || []).filter((x) => x.parent_id === c.id);
  return (
    <div data-testid="post-comment">
      <CommentBubble c={c} reactComment={reactComment} onReply={() => setReplyTo(replyTo === c.id ? null : c.id)} />
      {replyTo === c.id && (
        <form onSubmit={(e) => { e.preventDefault(); postComment(replyText, c.id); }} className="ml-9 mt-2 flex gap-2">
          <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={`Reply to ${c.user_name}…`} className="h-9 rounded-full" data-testid="comment-reply-input" autoFocus />
          <Button type="submit" size="icon" className="h-9 w-9 shrink-0 rounded-full" data-testid="comment-reply-send"><PaperPlaneRight size={15} weight="fill" /></Button>
        </form>
      )}
      {replies.length > 0 && (
        <div className="ml-9 mt-2 space-y-2">
          {replies.map((rp) => (
            <CommentBubble key={rp.id} c={rp} reactComment={reactComment} onReply={null} />
          ))}
        </div>
      )}
    </div>
  );
}

function FindPostCard({ r, index }) {
  const { user } = useAuth();
  const isCreator = user && r.creator_id === user.id;
  const initials = (r.creator_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const tags = [r.institution, r.course, r.grad_year && `Class of ${r.grad_year}`, r.batch, r.last_known_location || r.city].filter(Boolean);
  const when = new Date(r.created_at);
  const [rc, setRc] = useState(r.reaction_counts || {});
  const [mine, setMine] = useState(r.my_reaction || null);
  const [muted, setMuted] = useState(!!r.muted);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [cText, setCText] = useState("");
  const [cCount, setCCount] = useState(r.comments_count || 0);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const total = Object.values(rc).reduce((a, b) => a + b, 0);

  const toggleMute = async () => {
    try {
      const { data } = await api.post(`/find-requests/${r.id}/mute`);
      setMuted(data.muted);
      toast.success(data.muted ? "Comment notifications muted" : "Comment notifications on");
    } catch (e) { toast.error("Could not update notifications"); }
  };

  const react = async (emoji) => {
    const { data } = await api.post(`/find-requests/${r.id}/react`, { emoji });
    setRc(data.reaction_counts); setMine(data.my_reaction);
  };
  const openComments = async () => {
    setShowComments((s) => !s);
    if (comments === null) {
      const { data } = await api.get(`/find-requests/${r.id}/comments`);
      setComments(data); setCCount(data.length);
    }
  };
  const postComment = async (text, parentId = null) => {
    if (!text.trim()) return;
    const { data } = await api.post(`/find-requests/${r.id}/comments`, { text, parent_id: parentId });
    setComments((c) => [...(c || []), data]); setCCount((n) => n + 1);
    if (parentId) { setReplyTo(null); setReplyText(""); } else setCText("");
  };
  const reactComment = async (cid, emoji) => {
    const { data } = await api.post(`/find-comments/${cid}/react`, { emoji });
    setComments((cs) => cs.map((x) => x.id === cid ? { ...x, reaction_counts: data.reaction_counts, my_reaction: data.my_reaction } : x));
  };

  return (
    <Card className="overflow-hidden border-border/60 shadow-sm animate-fade-up" style={{ animationDelay: `${index * 0.04}s` }} data-testid="find-request-card">
      <div className="flex items-center gap-3 p-4 pb-3">
        <Avatar className="h-11 w-11"><AvatarImage src={fileUrl(r.creator_avatar)} /><AvatarFallback className="bg-accent">{initials}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-sm"><span className="font-semibold">{r.creator_name}</span> <span className="text-muted-foreground">is looking for someone</span></p>
          <p className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={12} /> {when.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
        </div>
        <Badge variant={r.status === "open" ? "secondary" : "outline"} className="rounded-full font-normal capitalize">{r.status}</Badge>
        {isCreator && (
          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-muted-foreground" onClick={toggleMute} title={muted ? "Notifications muted" : "Mute comment notifications"} data-testid="post-mute-toggle">
            {muted ? <BellSlash size={16} /> : <Bell size={16} />}
          </Button>
        )}
      </div>

      <div className="px-4 pb-3">
        <div className="flex items-start gap-3 rounded-xl bg-muted/40 p-4">
          {r.photo_file_id ? <img src={fileUrl(r.photo_file_id)} alt={r.subject_name} className="h-16 w-16 rounded-lg object-cover" />
            : <span className="grid h-16 w-16 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground"><Detective size={28} weight="duotone" /></span>}
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-medium leading-tight">{r.subject_name}{r.nickname && <span className="text-muted-foreground font-normal"> "{r.nickname}"</span>}</h3>
            {tags.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {tags.slice(0, 4).map((t, j) => <span key={j} className="rounded-full bg-background px-2.5 py-0.5 text-xs text-muted-foreground">{t}</span>)}
              </div>
            )}
          </div>
        </div>
        {r.memories && <p className="mt-3 text-sm leading-relaxed text-foreground/90">{r.memories}</p>}
        {r.other_clues && <p className="mt-2 whitespace-pre-line text-sm italic leading-relaxed text-muted-foreground">{r.other_clues}</p>}
      </div>

      {/* reaction summary */}
      {total > 0 && (
        <div className="flex items-center gap-1 px-4 pb-1 text-xs text-muted-foreground">
          {Object.entries(rc).map(([e, n]) => <span key={e}>{e} {n}</span>)}
        </div>
      )}

      {/* action bar */}
      <div className="flex items-center justify-between border-t border-border/50 px-2 py-1.5">
        <div className="flex items-center">
          <div className="group relative">
            <Button size="sm" variant="ghost" className={`gap-1.5 rounded-full ${mine ? "text-secondary" : "text-muted-foreground"}`} data-testid="post-react-toggle">
              {mine || "🙂"} React
            </Button>
            <div className="absolute bottom-full left-0 z-10 mb-1 hidden gap-1 rounded-full border border-border/60 bg-card p-1 shadow-lg group-hover:flex">
              {REACTIONS.map((e) => (
                <button key={e} onClick={() => react(e)} className={`rounded-full px-1.5 text-lg transition-transform hover:scale-125 ${mine === e ? "bg-secondary/15" : ""}`} data-testid={`react-${e}`}>{e}</button>
              ))}
            </div>
          </div>
          <Button size="sm" variant="ghost" className="gap-1.5 rounded-full text-muted-foreground" onClick={openComments} data-testid="post-comment-toggle">
            <ChatCircle size={16} /> {cCount}
          </Button>
        </div>
        <Link to={`/find/r/${r.public_slug}`} data-testid="find-request-open">
          <Button size="sm" variant="ghost" className="gap-1.5 rounded-full text-secondary hover:bg-secondary/10">
            <HandWaving size={16} weight="fill" /> {r.status === "open" ? "I can help" : "View"} · {r.responses_count}
          </Button>
        </Link>
      </div>

      {/* comments */}
      {showComments && (
        <div className="border-t border-border/50 bg-muted/20 p-4" data-testid="post-comments">
          <div className="space-y-3">
            {comments && comments.length === 0 && <p className="text-xs text-muted-foreground">No comments yet. Start the conversation.</p>}
            {(comments || []).filter((c) => !c.parent_id).map((c) => (
              <CommentRow key={c.id} c={c} comments={comments} reactComment={reactComment}
                replyTo={replyTo} setReplyTo={setReplyTo} replyText={replyText} setReplyText={setReplyText} postComment={postComment} />
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); postComment(cText); }} className="mt-3 flex gap-2">
            <Input value={cText} onChange={(e) => setCText(e.target.value)} placeholder="Write a comment…" className="rounded-full" data-testid="post-comment-input" />
            <Button type="submit" size="icon" className="h-10 w-10 shrink-0 rounded-full" data-testid="post-comment-send"><PaperPlaneRight size={16} weight="fill" /></Button>
          </form>
        </div>
      )}
    </Card>
  );
}

function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject_name: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState({ institution: "", grad_year: "", location: "" });
  const [showFilters, setShowFilters] = useState(false);
  const photoRef = useRef();

  const load = () => api.get("/find-requests", { params: { mine, ...filters } }).then((r) => setRequests(r.data));
  useEffect(() => { load(); }, [mine, filters]);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_file_id: data.id }));
      toast.success("Photo attached");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.subject_name) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/find-requests", form);
      toast.success("Search request posted");
      setOpen(false); setForm({ subject_name: "" });
      setMine(true); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button variant={!mine ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setMine(false)} data-testid="find-tab-open">Open searches</Button>
          <Button variant={mine ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setMine(true)} data-testid="find-tab-mine">My requests</Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="create-find-request-btn"><Plus size={16} /> Help me find someone</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Help me find someone</DialogTitle>
              <DialogDescription>Share whatever you remember — the community and our matching engine will help.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {REQ_FIELDS.map(([k, label]) => (
                <div key={k} className="space-y-1.5"><Label className="text-xs">{label}</Label>
                  <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`req-${k}`} /></div>
              ))}
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Memories</Label>
                <Textarea rows={2} value={form.memories || ""} onChange={(e) => setForm({ ...form, memories: e.target.value })} placeholder="Shared classes, moments, stories you remember…" data-testid="req-memories" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Freeform notes</Label>
                <Textarea rows={3} value={form.other_clues || ""} onChange={(e) => setForm({ ...form, other_clues: e.target.value })} placeholder="Anything else that might help — write freely…" data-testid="req-freeform" /></div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Photo of the person (optional)</Label>
                <div className="flex items-center gap-3">
                  {form.photo_file_id
                    ? <img src={fileUrl(form.photo_file_id)} alt="attached" className="h-16 w-16 rounded-lg object-cover" />
                    : <span className="grid h-16 w-16 place-items-center rounded-lg border border-dashed border-border text-muted-foreground"><Camera size={22} /></span>}
                  <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} data-testid="req-photo-input" />
                  <Button type="button" variant="outline" size="sm" className="rounded-full gap-1.5" disabled={uploading} onClick={() => photoRef.current.click()} data-testid="req-photo-btn">
                    <Camera size={15} /> {uploading ? "Uploading…" : form.photo_file_id ? "Change photo" : "Attach photo"}
                  </Button>
                  {form.photo_file_id && <button type="button" onClick={() => setForm({ ...form, photo_file_id: null })} className="text-muted-foreground hover:text-destructive"><X size={16} /></button>}
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} className="rounded-full" data-testid="req-submit">{saving ? "Posting…" : "Post request"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mx-auto mt-4 max-w-2xl">
        <button onClick={() => setShowFilters((s) => !s)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="find-filter-toggle">
          <FunnelSimple size={16} /> Filter board {(filters.institution || filters.grad_year || filters.location) && <span className="rounded-full bg-secondary/15 px-2 text-xs text-secondary">active</span>}
        </button>
        {showFilters && (
          <div className="mt-3 grid grid-cols-1 gap-3 rounded-xl border border-border/60 bg-card p-4 sm:grid-cols-3">
            <div className="space-y-1"><Label className="text-xs">Institution</Label><Input value={filters.institution} onChange={(e) => setFilters({ ...filters, institution: e.target.value })} data-testid="filter-institution" /></div>
            <div className="space-y-1"><Label className="text-xs">Graduation year</Label><Input value={filters.grad_year} onChange={(e) => setFilters({ ...filters, grad_year: e.target.value })} data-testid="filter-grad_year" /></div>
            <div className="space-y-1"><Label className="text-xs">Location</Label><Input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} data-testid="filter-location" /></div>
            {(filters.institution || filters.grad_year || filters.location) && (
              <button onClick={() => setFilters({ institution: "", grad_year: "", location: "" })} className="text-left text-xs text-secondary hover:underline sm:col-span-3" data-testid="filter-clear">Clear filters</button>
            )}
          </div>
        )}
      </div>

      <div className="mx-auto mt-5 max-w-2xl space-y-5">
        {requests.length === 0 && <p className="text-center text-muted-foreground">{mine ? "You haven't posted any requests yet." : "No open searches match — try clearing filters or post one."}</p>}
        {requests.map((r, i) => <FindPostCard key={r.id} r={r} index={i} />)}
      </div>
    </div>
  );
}

export default function FindColleagues() {
  return (
    <main className="mx-auto max-w-7xl px-5 py-8">
      <div className="flex items-center gap-2"><Detective className="text-secondary" size={22} weight="duotone" /><p className="overline text-secondary">Reconnect</p></div>
      <h1 className="mt-1 font-heading text-3xl font-light tracking-tight sm:text-4xl">Find My Colleagues</h1>
      <p className="mt-2 text-muted-foreground">Search the global alumni network — or post a request to reconnect with someone you've lost touch with.</p>

      <Tabs defaultValue="search" className="mt-6">
        <TabsList>
          <TabsTrigger value="search" data-testid="reconnect-tab-search"><MagnifyingGlassPlus size={16} className="mr-1.5" /> Advanced search</TabsTrigger>
          <TabsTrigger value="requests" data-testid="reconnect-tab-requests"><Detective size={16} className="mr-1.5" /> Missing people</TabsTrigger>
        </TabsList>
        <TabsContent value="search" className="mt-6"><SearchTab /></TabsContent>
        <TabsContent value="requests" className="mt-6"><RequestsTab /></TabsContent>
      </Tabs>
    </main>
  );
}
